import React, { useState } from 'react';

/**
 * "Shop by Category" section block: themed surface, centered header,
 * responsive grid of category cards (preview image + name + item
 * count), and a footer with a "View All Products" CTA and an optional
 * "Show More / Show Less" toggle.
 *
 * Why bundled in one file: only used on HomePage, and pulling the
 * card markup, the icons, and the constants apart would just scatter
 * one block across more files. Local `showAll` state lives here since
 * it's purely UI-only — the parent doesn't need to know whether the
 * list is expanded.
 *
 * The selection / "view all" handlers stay as callbacks because the
 * side effects span multiple pieces of HomePage state (category
 * filter, pagination reset, scroll-into-view of the products grid).
 * The parent wires the actual behavior; this component only emits.
 */

const CARD_RADIUS = '12px';
const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.06)';
const CARD_HOVER_SHADOW = '0 8px 25px rgba(0,0,0,0.08)';
const SECTION_MAX_WIDTH = '1200px';
const SECTION_HPAD = '1.5rem';

const TagIcon = ({ size = 32, color = '#9ca3af' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const ArrowRightIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const SectionHeader = ({ title, subtitle, themeStyles, theme }) => (
  <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
    <h2 style={{
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: '700',
      color: themeStyles?.text || '#111827',
      marginBottom: '0.5rem',
      fontFamily: theme?.fonts?.heading || 'inherit'
    }}>
      {title}
    </h2>
    <div style={{
      width: '40px',
      height: '3px',
      backgroundColor: themeStyles?.primary || '#2563eb',
      borderRadius: '2px',
      margin: '0 auto 0.75rem'
    }} />
    {subtitle && (
      <p style={{
        fontSize: '1rem',
        color: themeStyles?.textMuted || '#6b7280',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        {subtitle}
      </p>
    )}
  </div>
);

const CategoryCard = ({ category, imgUrl, itemCount, isSelected, onClick, themeStyles, theme }) => (
  <div
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    style={{
      backgroundColor: themeStyles?.background || 'white',
      borderRadius: CARD_RADIUS,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      border: `1px solid ${isSelected ? (themeStyles?.primary || '#2563eb') : (themeStyles?.border || '#e5e7eb')}`,
      boxShadow: isSelected ? `0 0 0 1px ${themeStyles?.primary || '#2563eb'}` : CARD_SHADOW,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = CARD_HOVER_SHADOW;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = isSelected ? `0 0 0 1px ${themeStyles?.primary || '#2563eb'}` : CARD_SHADOW;
    }}
  >
    <div style={{
      height: '120px',
      backgroundColor: themeStyles?.backgroundAlt || '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {imgUrl ? (
        <img src={imgUrl} alt={category} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <TagIcon size={32} color={themeStyles?.textMuted || '#9ca3af'} />
      )}
    </div>

    <div style={{ padding: '0.875rem' }}>
      <h3 style={{
        fontSize: '0.9375rem',
        fontWeight: '600',
        color: themeStyles?.text || '#111827',
        marginBottom: '0.375rem',
        textTransform: 'capitalize',
        fontFamily: theme?.fonts?.heading || 'inherit'
      }}>
        {category}
      </h3>
      <span style={{
        display: 'inline-block',
        padding: '0.125rem 0.5rem',
        borderRadius: '10px',
        backgroundColor: themeStyles?.backgroundAlt || '#f3f4f6',
        fontSize: '0.75rem',
        fontWeight: '500',
        color: themeStyles?.textMuted || '#6b7280'
      }}>
        {itemCount} {itemCount === 1 ? 'item' : 'items'}
      </span>
    </div>
  </div>
);

const ShopByCategory = ({
  categories,
  activeProducts,
  selectedCategory,
  onSelectCategory,
  onViewAll,
  maxBeforeCollapse = 8,
  themeStyles,
  theme
}) => {
  const [showAll, setShowAll] = useState(false);

  if (!categories || categories.length === 0) return null;

  const visible = showAll ? categories : categories.slice(0, maxBeforeCollapse);
  const isViewAllActive = selectedCategory === 'all';

  return (
    <div style={{
      backgroundColor: themeStyles?.backgroundAlt || '#fafafa',
      padding: '4rem 0',
      borderBottom: `1px solid ${themeStyles?.border || '#e5e7eb'}`
    }}>
      <div style={{ maxWidth: SECTION_MAX_WIDTH, margin: '0 auto', padding: `0 ${SECTION_HPAD}` }}>
        <SectionHeader
          title="Shop by Category"
          subtitle="Discover our curated collection across different categories"
          themeStyles={themeStyles}
          theme={theme}
        />

        {/* Max 3 cards per row, with the last (partial) row
            centered. Auto-fill grid would have packed 5+ per row on
            wide screens and left orphans aligned to the left when
            counts didn't divide evenly. Flex + justify-content:
            center handles both cleanly. */}
        <div className="sf-cat-grid">
          {visible.map((category) => {
            const categoryProducts = (activeProducts || []).filter(p => p.category === category);
            const previewProduct = categoryProducts.find(p => p.primaryImage || (p.gallery && p.gallery.length > 0));
            const imgUrl = previewProduct ? (previewProduct.primaryImage || previewProduct.gallery[0]) : null;

            return (
              <div key={category} className="sf-cat-cell">
                <CategoryCard
                  category={category}
                  imgUrl={imgUrl}
                  itemCount={categoryProducts.length}
                  isSelected={selectedCategory === category}
                  onClick={() => onSelectCategory?.(category)}
                  themeStyles={themeStyles}
                  theme={theme}
                />
              </div>
            );
          })}
        </div>

        <style>{`
          .sf-cat-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 1rem;
          }
          .sf-cat-cell {
            flex: 0 1 calc((100% - 2rem) / 3);
            min-width: 0;
            max-width: 360px;
            display: flex;
          }
          .sf-cat-cell > * {
            width: 100%;
          }
          @media (max-width: 768px) {
            .sf-cat-cell {
              flex: 0 1 calc((100% - 1rem) / 2);
            }
          }
          @media (max-width: 480px) {
            .sf-cat-cell {
              flex: 1 1 100%;
              max-width: 360px;
            }
          }
        `}</style>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          alignItems: 'center',
          marginTop: '2rem'
        }}>
          <button
            onClick={() => onViewAll?.()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.75rem',
              fontSize: '0.9375rem',
              fontWeight: '600',
              color: isViewAllActive ? 'white' : (themeStyles?.primary || '#2563eb'),
              backgroundColor: isViewAllActive ? (themeStyles?.primary || '#2563eb') : (themeStyles?.background || 'white'),
              border: `1px solid ${themeStyles?.primary || '#2563eb'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = themeStyles?.primary || '#2563eb';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              if (!isViewAllActive) {
                e.currentTarget.style.backgroundColor = themeStyles?.background || 'white';
                e.currentTarget.style.color = themeStyles?.primary || '#2563eb';
              }
            }}
          >
            <span>View All Products</span>
            <ArrowRightIcon size={16} />
          </button>

          {categories.length > maxBeforeCollapse && (
            <button
              onClick={() => setShowAll(prev => !prev)}
              style={{
                padding: '0.5rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: themeStyles?.textMuted || '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '3px'
              }}
            >
              {showAll
                ? 'Show Less'
                : `Show More (${categories.length - maxBeforeCollapse} more)`
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopByCategory;
